/**
 * SalesCampaignSheet_Action Component
 * Version: 1.2.0
 * 
 * Changelog v1.2.0:
 * - AJOUT : Synchronisation bidirectionnelle avec le VideoPlayer
 * - Quand l'utilisateur modifie timecode ou duration, currentMarker est mis à jour
 * - currentMarker est recalculé uniquement quand les valeurs changent vraiment
 * - Utilisation d'une ref (isInternalUpdate) pour éviter les boucles infinies
 * - Les modifications depuis les champs texte se répercutent visuellement dans le player
 * 
 * Changelog v1.1.0:
 * - AJOUT : fieldsRef pour partager les données en temps réel avec VideoPlayerWithAdvertisingSupport
 * - useEffect pour synchroniser fieldsRef.current avec fields
 * - Passage de fieldsRef à VideoPlayerWithAdvertisingSupport via currentSalesActionRef
 * - Permet la mise à jour en temps réel de l'Advertisement dans la vidéo lors de l'édition
 * 
 * Changelog v1.0.0:
 * - Version initiale du composant d'édition de SalesAction
 */

import   React                             ,
       { useState                          ,
	     useEffect                         ,
         useCallback                       ,
		 useMemo                           ,
		 useRef                            }  from 'react';

import   classNames                           from 'classnames';

import   styles                               from './SalesCampaignSheet_Action.module.scss';

import   Field                                from '../../widget/inputs/Field';
import   FieldColor                           from '../../widget/inputs/FieldColor';
import   FieldMedia                           from '../../widget/inputs/FieldMedia';
import   Select                               from '../../widget/inputs/Select';
import   WidgetFrame                          from '../../widget/navigation/WidgetFrame';
import   Toggle                               from '../../widget/inputs/Toggle';
import   Trigger                              from '../../widget/actuators/Trigger';
import   Window                               from '../../widget/windows/window';
import   CollapsiblePanel                     from '../../widget/windows/CollapsiblePanel';
import { generateAdvertisementCanvas          }  from '../../widget/images/AdvertisementCanvasGenerator';
import   Icon                                 from '../../widget/images/Icon';
import   Carousel2                            from '../../widget/navigation/Carousel2';

import   MediaSelector                        from '../mediaManagment/MediaSelector';
import   ProductSelector                      from '../productManagment/ProductSelector';
import   VideoPlayerWithAdvertisingSupport    from '../marketingCampaignManagment/VideoPlayerWithAdvertisingSupport';
import { Advertisement                     }  from '../marketingCampaignManagment/Advertisement';

import { Video                             }  from '../../../dataTypes/mediaManagment/video_types';

import { Media                             , 
         initializedMedia                  , 
		 MediaCategory                     }  from '../../../dataTypes/mediaManagment/types';

import { Product                           , 
         initializedProduct                   }  from '../../../dataTypes/productManagment/types';


import { SalesCampaign                     , 
         SalesAction            }  from '../../../dataTypes/salesCampaignManagment/types';


import { WWW_URLS                          }  from '../../../backendServices/urls';

import { getSalesActions                   }  from '../../../backendServices/salesCampaignManagment/api_salesCampaign';

import { useTypedSelector                  ,
         useTypedDispatch                  } from '../../../store/useTypeSelector';
import { fetchProductList               } from '../../../store/productManagment/actions';
import { fetchProductCategories            } from '../../../store/productCategoryManagment/actions';

import { getImageById             ,
         downloadImageBlob            }  from '../../../backendServices/mediaManagment/api_image';

import { createUuid               }  from '../../../backendServices/uuidManagment/api';
		 
import { timeStringToSeconds      } from '../../../dataTypes/timeManagment/utils';
		 
	 
	 
export interface SalesCampaignSheet_ActionComponent_Props {
  isEditMode?            : boolean;
  salesAction?           : SalesAction;
  medias                 : Media[];
  mediaCategories        : MediaCategory[];
  existingSalesCampaigns : SalesCampaign[];
  salesCampaign?         : SalesCampaign;
  title                  : string;
  width?                 : string;
  onChange?              : (newSalesAction: SalesAction) => void;
  onResize?              : () => void;
  onDelete?              : () => void;
  open?                  : boolean;
  name?                  : string;
  showErrorMessages?     : boolean;
  version?               : number
}

export const SalesCampaignSheet_ActionComponent = ({ isEditMode             = false  ,
                                            salesAction                     ,
                                            medias                          ,
                                            mediaCategories                 ,
                                            existingSalesCampaigns          ,
                                            title                           ,
                                            width                  = '100%' ,
                                            onChange                        ,
                                            onResize                        ,
                                            onDelete                        ,
                                            open                            ,
                                            name                            ,
											showErrorMessages      = true   ,
											version                = 0      }: SalesCampaignSheet_ActionComponent_Props) => {

                                                const dispatch = useTypedDispatch();

  // Accéder au state
  const products   = useTypedSelector(state => state.productListReducer.products);
  // Valeur inutilisée, mais l'abonnement est conservé : le retirer changerait
  // la fréquence de rendu du composant.
  useTypedSelector(state => state.productListReducer.loading);
  const categories = useTypedSelector(state => state.productCategoryReducer.categories);
  const { authentication } = useTypedSelector(state => state.authenticationReducer);

  // Charger les données
  useEffect(() => {
    dispatch(fetchProductList({}, '', false, authentication.admin?.id));
    dispatch(fetchProductCategories());
  }, [dispatch]);

  // Utiliser les données
  const productArray = Array.from(products.values());
  const categoryArray = Array.from(categories.values());

  let initialSalesAction = salesAction //isEditMode && salesAction ? salesAction : initializedSalesAction();

  const [fields, setFields] = useState<SalesAction>({
    name                 : isEditMode ? (initialSalesAction?.name   ?? (name ?? '')) : (name ?? ''),
    id                   : initialSalesAction?.id                   ?? 0,
    active               : initialSalesAction?.active               ?? true,
    creationDate         : initialSalesAction?.creationDate         ?? new Date(),
    mediaId              : initialSalesAction?.mediaId              ?? 0,
    productId            : initialSalesAction?.productId            ?? 0,
    productVariant       : isEditMode ? (initialSalesAction?.productVariant ?? 0) : 0,
    productImage         : isEditMode ? (initialSalesAction?.productImage ?? 0) : 0,
    timecode             : initialSalesAction?.timecode             ?? '',
    duration             : initialSalesAction?.duration             ?? '',
    adPosition           : initialSalesAction?.adPosition           ?? 'right',
    adWidth              : initialSalesAction?.adWidth              ?? 250,
    onlyQRcode           : initialSalesAction?.onlyQRcode           ?? false,
    comment1             : initialSalesAction?.comment1             ?? '',
    comment2             : initialSalesAction?.comment2             ?? '',
    shapeColor1          : initialSalesAction?.shapeColor1          ?? '',
    shapeColor2          : initialSalesAction?.shapeColor2          ?? '',
    backgroundColor1     : initialSalesAction?.backgroundColor1     ?? '',
    backgroundColor2     : initialSalesAction?.backgroundColor2     ?? '',
	withImage            : initialSalesAction?.withImage            ?? true,
    alternateImageId     : initialSalesAction?.alternateImageId     ?? 0,
    alternateImageUrl    : initialSalesAction?.alternateImageUrl    ?? '',
    alternateImageFile   : initialSalesAction?.alternateImageFile   ?? null,
    redirectionId        : initialSalesAction?.redirectionId        ?? 0,
    redirectionTargetUrl : initialSalesAction?.redirectionTargetUrl ?? '',
    redirectionUuid      : initialSalesAction?.redirectionUuid      ?? '',
    redirectionImageId   : initialSalesAction?.redirectionImageId   ?? 0,
    redirectionImageUrl  : initialSalesAction?.redirectionImageUrl  ?? null,
    redirectionImageFile : initialSalesAction?.redirectionImageFile ?? null,
    resultImageFile      : initialSalesAction?.resultImageFile      ?? null,
  });

  // ✅ AJOUT v1.1.0 : Créer une ref pour partager les fields en temps réel
  const fieldsRef = useRef<SalesAction>(fields);

  // ✅ AJOUT v1.2.0 : Ref pour éviter les boucles de synchronisation
  const isInternalUpdate = useRef(false);
  
  // ✅ AJOUT : Garder une référence du dernier marker calculé
  const lastCalculatedMarker = useRef<{ start: number; end: number } | null>(null);

  const [ alternateImageName     , setAlternateImageName     ] = useState (initialSalesAction?.alternateImageUrl  ?? '')
  const [ redirectionImageName   , setRedirectionImageName   ] = useState (initialSalesAction?.redirectionImageUrl  ?? '')

  const [ mediaErrorMessage      , setMediaErrorMessage      ] = useState('');
  const [ productErrorMessage    ] = useState('');
  const [ timecodeErrorMessage   , setTimecodeErrorMessage   ] = useState('');
  const [ durationErrorMessage   , setDurationErrorMessage   ] = useState('');


  const [ salesActions           , setSalesActions           ] = useState<SalesAction[]>([]);
  const [ showRequest            , setShowRequest            ] = useState(false);
  const [ requestMessage         , setRequestMessage         ] = useState(<></>);

  const [ urlsImage              , setUrlsImage              ] = useState<string[]> ([])
  const [ variantsName           , setVariantsName           ] = useState<string[]> ([])
  const [ externalLink           , setExternalLink           ] = useState<boolean>  ( (initialSalesAction?.redirectionId ?? 0) !== 0 );

  const isInitialProductSelection = useRef(true);
  const isManualVariantChange = useRef(false);

  const onChangeRef = useRef(onChange);

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  // ✅ AJOUT v1.1.0 : Synchroniser la ref avec les fields
  useEffect(() => {
    fieldsRef.current = fields;
  }, [fields]);

  const updateFields = useCallback((updates: Partial<SalesAction> | ((prev: SalesAction) => Partial<SalesAction>)) => {
  
    console.log (fields)
  
    setFields(prevFields => {
      const newUpdates = typeof updates === 'function' ? updates(prevFields) : updates;
      const updatedFields = { ...prevFields, ...newUpdates };
    
      if (onChangeRef.current) 
	   {
        onChangeRef.current(updatedFields);
       }
    
      return updatedFields;
    });
  }, []); 

  const getChoosenMedia = (id: number): Media => {

    if (!medias || medias.length === 0) 
	 {
      console.warn('getChoosenMedia: videos array is empty or not provided.');
      return initializedMedia();
     }

    for (let i = 0; i < medias.length; i++) 
	 {
      if (medias[i].id == id) 
	   {
        return medias[i];
       }
     }

    console.warn('getChoosenMedia: Media not found, returning initializedMedia.');
    return initializedMedia();
  };

  const getChoosenProduct = (id: number): Product => {

    if (!productArray || productArray.length === 0)
	 {
      console.warn('getChoosenProduct: products array is empty or not provided.');
      return initializedProduct();
     }

    for (let i = 0; i < productArray.length; i++)
	 {
      if (productArray[i].id == id)
	   {
        return productArray[i];
       }
     }

    console.warn('getChoosenProduct: Product not found, returning initializedProduct.');
    return initializedProduct();
  };

  const getSalesCampaign = (salesActionId: number) : SalesCampaign | null => {
	  
	let  salesCampaignFound : SalesCampaign | null = null
	
	for ( let c=0; c<existingSalesCampaigns.length; c++)
	 {
  	  for ( let a=0; a<existingSalesCampaigns[c].salesActions.length; a++)
	   {
	    if ( existingSalesCampaigns[c].salesActions[a].id == salesActionId )
		 {
		  salesCampaignFound = existingSalesCampaigns[c]
          break		  
		 }
	   }
	 }
	 
	return salesCampaignFound
  }

  const [ choosenMedia     , setChoosenMedia     ] = useState<Media>(isEditMode ? getChoosenMedia(fields.mediaId) : initializedMedia());
  const [ mediaSelection   , setMediaSelection   ] = useState(false);
  const [ mediaSelection2  , setMediaSelection2  ] = useState(false);
  const [ mediaSelection3  , setMediaSelection3  ] = useState(false);

  const [ choosenProduct   , setChoosenProduct   ] = useState<Product>(isEditMode ? getChoosenProduct(fields.productId) : initializedProduct());
  const [ productSelection , setProductSelection ] = useState(false);


  const searchImages = (product: Product, variantIndex: number): string[] => {
    let imgFiles: string[] = [];
    let cpt: number = 1;
    let cptStr: string = '';

    console.log ('searchImages', product, variantIndex)

    if (product && product.id !== 0 && product.variants && product.variants[variantIndex]) 
	 {
      for (let c = 0; c < product.variants[variantIndex].medias.length; c++) 
	   {
        cptStr = cpt.toString().padStart(4, "0");
        imgFiles.push(`${WWW_URLS.MediaServer}${product.publicRef}/images/${product.variants[variantIndex].code}-${cptStr}_BIG.webp`);
        cpt++;
       }
     }

    return imgFiles;
  };

  const searchVariantsName = (product: Product): string[] => {
    let names: string[] = [];

    if (product && product.id !== 0 && product.variants) 
	 {
      for (let c = 0; c < product.variants.length; c++) 
	   {
        names.push(`${product.variants[c].name}`);
       }
     }

    return names;
  };

useEffect(() => {
  if (choosenProduct && choosenProduct.id !== 0) {
    const variantToUse = 0
    const newImages = searchImages(choosenProduct, variantToUse);
    const newVariants = searchVariantsName(choosenProduct);

    setUrlsImage(newImages);
    setVariantsName(newVariants);

    if (!isEditMode && isInitialProductSelection.current) {
      updateFields({
        productId          : choosenProduct.id,
        productVariant     : 0,
        productImage       : 0,
        alternateImageUrl  : '',
        alternateImageFile : null,
      });
      setAlternateImageName('');
      isInitialProductSelection.current = false;
    } else if (isEditMode) {
      updateFields({
        productId          : choosenProduct.id,
        productVariant     : 0,
        productImage       : 0
	  });
    } else {
      updateFields({
        productId          : choosenProduct.id,
        productVariant     : 0,
        productImage       : 0
      });
    }
  }
}, [choosenProduct.id, updateFields, isEditMode]);

useEffect(() => {
  if (choosenProduct && choosenProduct.id !== 0 && fields.productVariant !== undefined) {
    const newImages = searchImages(choosenProduct, fields.productVariant);
    setUrlsImage(newImages);
    
    if (isManualVariantChange.current && fields.productId !== 0) {
      updateFields({
        productImage       : 0,
        alternateImageFile : null
      });
      isManualVariantChange.current = false;
    }
  }
}, [fields.productVariant, choosenProduct.id, fields.productId, updateFields]);

useEffect(() => {
	  
  const fetchSalesActions = async () => {

    if (choosenMedia !== null && choosenMedia.id !== 0) 
     {
      const salesActionsResult = await getSalesActions(choosenMedia.id);

      if (salesActionsResult.code === 1) 
       {
        setSalesActions(salesActionsResult.payload);

        if ( choosenMedia.id !== (initialSalesAction?.mediaId ?? 0) )
         {
          if (salesActionsResult.payload.length === 1 ) 
           {
            const salesActionAnalyzed = salesActionsResult.payload[0]
            const salesCampaignSearch = getSalesCampaign (salesActionAnalyzed.id)		
            const product = getChoosenProduct (salesActionAnalyzed.productId ?? 0)			  
            setShowRequest(true);
            setRequestMessage (
              <>
                Cette vidéo est déjà utilisée pour la campagne {salesCampaignSearch?.name ?? ""} avec 
                le produit {product?.name ?? ""} au timecode {salesActionAnalyzed.timecode ?? ""} avec 
                la durée {salesActionAnalyzed.duration ?? ""}<br/>
                Voulez-vous continuer ?
              </>
            );
           }
			 
          if (salesActionsResult.payload.length > 1) 
           {
            let listItems: JSX.Element[] = [];

            for (let a: number = 0; a < salesActionsResult.payload.length; a++) 
             {
              const salesActionAnalyzed = salesActionsResult.payload[a];
              const salesCampaignSearch = getSalesCampaign(salesActionAnalyzed.id);
              const product             = getChoosenProduct(salesActionAnalyzed.productId ?? 0);

              listItems.push (
                <li key={a}>
                  la campagne {salesCampaignSearch?.name ?? ""} avec le produit {product?.name ?? ""} au 
                  timecode {salesActionAnalyzed.timecode ?? ""} avec la durée {salesActionAnalyzed.duration ?? ""}
                </li>
              );
             }

            const message = (
              <div style={{ textAlign: 'justify' }}>
                <p>Cette vidéo est déjà utilisée pour les campagnes suivantes :</p>
                <ul style={{ paddingLeft: '1.5rem', listStyleType: 'disc' }}>
                  {listItems}
                </ul>
              </div>
            );

            setShowRequest(true);
            setRequestMessage(message);
           }
         }
       }
     }
  };

  fetchSalesActions();
}, [choosenMedia]);


  const loadImageBlob = async () => {

    try
     {
      const getImageByIdResult = await getImageById( fields.alternateImageId);

      if (getImageByIdResult.code === 1)
       {
        const completeImageStruct = await downloadImageBlob ( getImageByIdResult.payload);

        if (completeImageStruct.objectURL)
              {
			   console.log (completeImageStruct.objectURL)	  
				  
   	   	       setAlternateImageName (`${completeImageStruct.objectURL}`)
               updateFields({ alternateImageUrl: completeImageStruct.objectURL });
              }
         else {
               console.error("objectURL is undefined");
              }
       }
     }
    catch (error)
     {
      console.error("Erreur lors du chargement de l'image:", error);
     }
  };


  useEffect(() => {

    if ( isEditMode )
	 {
	  if ( fields.alternateImageId != null)
	   {
		loadImageBlob ()   

	   }
     }
	 
	 
  }, [isEditMode, fields.alternateImageFile, updateFields]);

const existingQrMarkers = useMemo(() => {
  return salesActions
    .filter(action => action.id !== fields.id)
    .map(action => ({
      start: timeStringToSeconds(action.timecode),
      end: timeStringToSeconds(action.timecode) + timeStringToSeconds(action.duration),
      id: action.id
    }));
}, [salesActions, fields.id]);

// ✅ AJOUT v1.2.0 : Calculer currentMarker à partir de timecode et duration
const currentMarker = useMemo(() => {
  console.log('🔄 SalesAction: currentMarker useMemo triggered', {
    timecode: fields.timecode,
    duration: fields.duration,
    isInternalUpdate: isInternalUpdate.current
  });

  // Ignorer si c'est une mise à jour venant du VideoPlayer
  // MAIS retourner le dernier marker calculé au lieu de null
  if (isInternalUpdate.current) {
    console.log('🔄 SalesAction: Skipping recalculation (internal update), returning last marker', lastCalculatedMarker.current);
    isInternalUpdate.current = false;
    return lastCalculatedMarker.current;
  }

  // Vérifier que timecode et duration sont valides
  if (!fields.timecode || !fields.duration) {
    console.log('🔄 SalesAction: Missing timecode or duration', { 
      timecode: fields.timecode, 
      duration: fields.duration 
    });
    lastCalculatedMarker.current = null;
    return null;
  }

  // Vérifier le format HH:MM:SS
  const regex = /^([01]\d|2[0-3]):([0-5]\d):([0-5]\d)$/;
  if (!regex.test(fields.timecode) || !regex.test(fields.duration)) {
    console.log('🔄 SalesAction: Invalid format', { 
      timecode: fields.timecode, 
      duration: fields.duration,
      timecodeValid: regex.test(fields.timecode),
      durationValid: regex.test(fields.duration)
    });
    lastCalculatedMarker.current = null;
    return null;
  }

  const start = timeStringToSeconds(fields.timecode);
  const durationInSeconds = timeStringToSeconds(fields.duration);
  const end = start + durationInSeconds;

  const newMarker = { start, end };

  console.log('🔄 SalesAction: currentMarker calculated', { 
    timecode: fields.timecode,
    duration: fields.duration,
    start, 
    durationInSeconds,
    end 
  });

  // Sauvegarder le marker calculé
  lastCalculatedMarker.current = newMarker;
  
  return newMarker;
}, [fields.timecode, fields.duration]);

  const testMedia  = ( mediaId : number ) : string => {

	if ( mediaId === 0)
	      {
	       return ("Il manque le média") 
	      }
	 else {
	       return ("") 
	      }
  }


  const testTimeCode = ( tc : string ) : string => {
	  
	if ( tc === '' )
	      {
	       return ("Il manque le timecode") 
	      }
	 else {
  		   const regex = /^([01]\d|2[0-3]):([0-5]\d):([0-5]\d)$/;
		   
           if ( regex.test (tc) === false )
		         {
	              return ("Le timecode doit respecter le format HH:MM:SS") 
		         }
		    else {
	              return ("") 
   		         }
 	      }  
  }

  const testDuration = ( td : string ) : string => {
	  
	if ( td === '' )
	      {
	       return ("Il manque la durée") 
	      }
	 else {
  		   const regex = /^([01]\d|2[0-3]):([0-5]\d):([0-5]\d)$/;
		   
           if ( regex.test (td) === false )
		        {
	             return ("La durée doit respecter le format HH:MM:SS") 
		        }
		   else {
	             return ("") 
		        }
   	      }  
  }

  useEffect(() => {
	  
	if ( showErrorMessages )
	 {
	  setMediaErrorMessage    ( testMedia    ( choosenMedia.id   ) )
      setTimecodeErrorMessage ( testTimeCode ( fields.timecode   ) );
      setDurationErrorMessage ( testDuration ( fields.duration   ) );
	 }	
	 
  }, [version, showErrorMessages, choosenMedia.id, fields.timecode, fields.duration]);

  useEffect(() => {
    return () => {
      if (fields.alternateImageUrl && fields.alternateImageUrl.startsWith('blob:')) 
	   {
        URL.revokeObjectURL(fields.alternateImageUrl);
       }
    };
  }, [fields.alternateImageUrl]);

  useEffect(() => {
    return () => {
      if (fields.redirectionImageUrl && fields.redirectionImageUrl.startsWith('blob:')) 
	   {
        URL.revokeObjectURL(fields.redirectionImageUrl);
       }
    };
  }, [fields.redirectionImageUrl]);

  /////// Bloc 1
  
  const handleActiveChange = useCallback((newActive: boolean) => {
    updateFields({ active: newActive });
  }, [updateFields]);

  const handleNameChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    updateFields({ name: e.target.value });
  }, [updateFields]);

  /////// Bloc 2 (Video)


  const handleDeleteChoosenProduct = (event: React.MouseEvent<HTMLOrSVGElement, MouseEvent>, id?: number, state?: boolean) => {
    setChoosenProduct(initializedProduct());
  }
  
  const handleMediaSelectorShow = () => {
    setMediaSelection(true);
  }

  const handleMediaSelectorHide = () => {
    setMediaSelection(false);
  }

  const handleMediaSelection = (id: number) => {
    setMediaSelection(false);
	
    let choice : Media = initializedMedia()

    for (let v = 0; v < medias.length; v++) {
      if (medias[v].id === id) {
        choice = medias[v]	
		
        setChoosenMedia(medias[v]);

        updateFields({ mediaId: medias[v].id });
        
        break;
      }
    }
	
    setMediaErrorMessage(testMedia(choice.id))
  };

  // ✅ MODIFICATION v1.2.0 : Marquer les mises à jour venant du VideoPlayer
  const handleVideoGoToStart = useCallback((startTime: number) => {
    console.log('Début marqué à:', startTime);

    const hours = Math.floor(startTime / 3600);
    const minutes = Math.floor((startTime % 3600) / 60);
    const seconds = Math.floor(startTime % 60);
    const timeString = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;

    // ✅ Marquer que c'est une mise à jour interne (pour éviter la boucle)
    isInternalUpdate.current = true;
    
    updateFields({ timecode: timeString });
    setTimecodeErrorMessage(testTimeCode(timeString));
  }, [updateFields]);

  const handleVideoGoToEnd = useCallback((endTime: number, startTime: number, duration: number) => {
    console.log('Fin marquée à:', endTime, 'Début:', startTime, 'Durée:', duration);

    const hours = Math.floor(duration / 3600);
    const minutes = Math.floor((duration % 3600) / 60);
    const seconds = Math.floor(duration % 60);
    const durationString = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;

    // ✅ Marquer que c'est une mise à jour interne (pour éviter la boucle)
    isInternalUpdate.current = true;
    
    updateFields({ duration: durationString });
    setDurationErrorMessage(testDuration(durationString));
  }, [updateFields]);

  /////// Bloc 3 (Produit)

  const handleProductSelectorShow = () => {
    setProductSelection(true);
  }

  const handleProductSelectorHide = () => {
    setProductSelection(false);
  }

  const handleProductSelection = (id: number) => {
    setProductSelection(false);

    for (let p = 0; p < productArray.length; p++)
	 {
      if (productArray[p].id === id)
	   {
        setChoosenProduct(productArray[p]);
        isInitialProductSelection.current = true;
        break;
       }
     }
  };

  const handleSelectedVariantChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    isManualVariantChange.current = true;
    updateFields({ 
      productVariant: e.target.selectedIndex-1,
      alternateImageFile: null 
    });
  }, [updateFields]);

  const handleSlideChange = useCallback((index: number) => {
    updateFields({ 
      productImage: index, 
      alternateImageFile: null 
    });
  }, [updateFields]);
  
  
    const renderChoosenProduct = (): JSX.Element => {
		
	console.log ('renderChoosenProduct', fields.productVariant, 	variantsName[fields.productVariant] )
		
    return (
      <>
        <div className={classNames(styles.productImagesCarousel)}>
          {renderImages()}
        </div>
        <div className={classNames(styles.line_2, styles.text)}>
  		    {
		     choosenProduct.variants.length > 1 
			 ?
             (
		      <div className = { classNames(styles.line_2b)} >
                <div className = { classNames(styles.variantChoice)} >
                  <Select choices   = {variantsName}
				          value     = {variantsName[fields.productVariant]}
				          onChange  = {handleSelectedVariantChange}
				          className = { classNames(styles.variantSelect)} />
                </div>
              </div>
		     )
			 :
			 (
  	  	      <div className = { classNames(styles.line_2a)} >
                <div className = { classNames(styles.title)} >
                  <div dangerouslySetInnerHTML={{ __html: choosenProduct.name }} /> 
                </div>
              </div>			 
			 )
		    }
			
			<Icon name="trashCan" onClick={handleDeleteChoosenProduct}/>
			
        </div>
      </>
    );
  };
  
  const generateImageItems = (): JSX.Element[] => {
    const items: JSX.Element[] = [];
  
    for (let i = 0; i < urlsImage.length; i++) 
	 {
      items.push (
                   <div key       = {i} 
				        className = { classNames(styles.carousel_image_item)} >
                     <img src = {urlsImage[i]} 
                          alt = {`Photo ${i + 1}`} />
                   </div>
                 );
     }
  
    return items;
  }; 

  const generateThumbnailItems = (): JSX.Element[] => {
    const items: JSX.Element[] = [];
  
    for (let i = 0; i < urlsImage.length; i++) 
	 {
      items.push (
                   <img src = {urlsImage[i]} 
                        alt = {`Photo ${i + 1}`} />
                 );
     }
  
    return items;
  };

  const MemoizedCarousel = useCallback(() => (
    <div className = {classNames(styles.carousel_image_block)}>
      <Carousel2 items                    = {generateImageItems()} 
                 minItemWidth             = {200}
                 maxItemWidth             = {550}
                 arrowColor               = '#000000' 
                 thumbnails               = {generateThumbnailItems()}
                 thumbnailsPosition       = 'bottom'
                 thumbnailsPositionMobile = 'bottom'
                 thumbnailWidth           = {80}
                 thumbnailHeight          = {80}
                 thumbnailWidthMobile     = {50}
                 thumbnailHeightMobile    = {50}
                 showThumbnails           = {true}
                 mobileBreakpoint         = {768} 
				 initialIndex             = {fields.productImage}
                 onSlideChange            = {handleSlideChange}  />
    </div>
  ), [urlsImage, fields.productImage, handleSlideChange]);

  const renderImages = (): JSX.Element => {
    
    switch (urlsImage.length) 
	 {
      case 0  : return ( 
	                     <div className={classNames(styles.no_image)}>
                           Pas d'images
                         </div>
                       );
    
      case 1  : return (
                         <div className={classNames(styles.single_image_container)}>
                           <img  src       = {urlsImage[0]}  
                                 className = {classNames(styles.single_image)} 
                                 alt       = "Product"  />
                         </div>
                       );
    
      default : return <MemoizedCarousel />;
     }
  }; 

  
  /////// Bloc 4 (config QRcode)

  const handleOnlyQRcodeChange = useCallback((newActive: boolean) => {
    updateFields({ onlyQRcode: newActive });
  }, [updateFields]);

  const handleComment1Change = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    updateFields({ comment1: e.target.value });
  }, [updateFields]);

  const handleShapeColor1Change = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    updateFields({ shapeColor1: e.target.value });
  }, [updateFields]);

  const handleBackgroundColor1Change = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    updateFields({ backgroundColor1: e.target.value });
  }, [updateFields]);

  const handleComment2Change = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    updateFields({ comment2: e.target.value });
  }, [updateFields]);

  const handleShapeColor2Change = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    updateFields({ shapeColor2: e.target.value });
  }, [updateFields]);

  const handleBackgroundColor2Change = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    updateFields({ backgroundColor2: e.target.value });
  }, [updateFields]);

  /////// Bloc 5 (image alternative)

  const handleWithImageChange = useCallback((newActive: boolean) => {
    updateFields({ withImage: newActive });
  }, [updateFields]);

  const handleAlternateImageChange = useCallback(( event      : React.ChangeEvent<HTMLInputElement> ,
                                       value      : string | number | File | Date       ,
                                       mediaType? : string                              ,
                                       duration?  : string                              ) => {
   
    const newValue = value instanceof File ? URL.createObjectURL(value) : String(value);
    const newName  = event.target.value.split("\\").pop() || event.target.value;

    setAlternateImageName(newName);

    updateFields(prev => ({
      alternateImageUrl: newValue,
      alternateImageFile: value instanceof File ? value : prev.alternateImageFile,
    }));
  }, [updateFields]);

  const handleAlternateImageDelete = useCallback(() => {
    updateFields({ 
      alternateImageUrl: '', 
      alternateImageFile: null 
    });
  }, [updateFields]);

  const handleAlternateImageLibrary = () => {
	setMediaSelection2(true)  
  }


  const handleMediaSelector2Hide = () => {
    setMediaSelection2(false);
  }

  const handleMediaSelection2 = (id: number) => {
  
    setMediaSelection2(false);
  
    (async () => {
      for (let v = 0; v < medias.length; v++) 
	   {
        if (medias[v].id === id) 
		 {
          const selectedMedia = medias[v];
        
          try 
		   {
            const getImageByIdResult = await getImageById(selectedMedia.id);
          
            if (getImageByIdResult.code === 1) 
			      {
                   const completeImageStruct = await downloadImageBlob ( getImageByIdResult.payload,
                                                                        (progress) => { console.log('Téléchargement de l\'image:', progress); } );
            
                   if (completeImageStruct.objectURL) 
			             {
                          const blobUrl = completeImageStruct.objectURL;
                          const fileName = completeImageStruct.imageFile ?? '';
              
                          updateFields({
                            alternateImageUrl: blobUrl,
                            alternateImageFile: null,
                          });
              
                          setAlternateImageName(fileName);
                         } 
			        else {
                          console.error("objectURL is undefined pour l'image", selectedMedia.id);
                         }
                  } 
			 else {
                   console.error("Erreur lors de la récupération de l'image:", getImageByIdResult.comment);
                  }
           } 
		  catch (error) 
		   {
            console.error("Erreur lors du téléchargement de l'image:", error);
           }
        
        break;
      }
    }
  })();
}

  /////// Bloc 5 (redirection)

const handleExternalLinkChange = (newActive: boolean) => {
	
  console.log ('handleExternalLinkChange')
	
	
  setExternalLink(newActive);
  
  if (newActive) {
    if (fields.redirectionUuid === '') {
      (async () => {
        const redirectionUuidGenerationResult = await createUuid();
        
        if (redirectionUuidGenerationResult.code === 1) {
          updateFields({ redirectionUuid: redirectionUuidGenerationResult.payload });
        }
      })();
    }
  }
};

  const handleRedirectionTargetUrlChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    updateFields({ redirectionTargetUrl: e.target.value });
  }, [updateFields]);

  const handleMediaSelector3Hide = () => {
    setMediaSelection3(false);
  }

  const handleMediaSelection3 = (id: number) => {
    setMediaSelection3(false);
  
    (async () => {
    for (let v = 0; v < medias.length; v++) 
	 {
      if (medias[v].id === id) 
	   {
        const selectedMedia = medias[v];
        
        try 
		 {
          const getImageByIdResult = await getImageById(selectedMedia.id);
          
          if (getImageByIdResult.code === 1) 
		        {
                 const completeImageStruct = await downloadImageBlob ( getImageByIdResult.payload,
                                                                       (progress) => { console.log('Téléchargement de l\'image de redirection:', progress); } );
            
                 if (completeImageStruct.objectURL) 
			           {
                        const blobUrl = completeImageStruct.objectURL;
              
                        updateFields({
                          redirectionImageUrl: blobUrl,
                          redirectionImageFile: null,
                        });
                       } 
		          else {
                        console.error("objectURL is undefined pour l'image de redirection", selectedMedia.id);
                       }
                } 
		   else {
                 console.error("Erreur lors de la récupération de l'image de redirection:", getImageByIdResult.comment);
                }
         }
		catch (error) 
		 {
          console.error("Erreur lors du téléchargement de l'image de redirection:", error);
         }
        
        break;
       }
     }
    })();
  }

 
  const handleRedirectionImageChange = useCallback(( event      : React.ChangeEvent<HTMLInputElement> ,
                                                      value      : string | number | File | Date       ,
                                                      mediaType? : string                              ,
                                       duration?  : string                              ) => {
   
    const newValue = value instanceof File ? URL.createObjectURL(value) : String(value);
    const newName  = event.target.value.split("\\").pop() || event.target.value;

    setRedirectionImageName(newName);

    updateFields(prev => ({
      redirectionImageUrl: newValue,
      redirectionImageFile: value instanceof File ? value : prev.alternateImageFile,
    }));
  }, [updateFields]);

  const handleRedirectionImageDelete = useCallback(() => {
    updateFields({ 
      redirectionImageUrl: '', 
      redirectionImageFile: null 
    });
  }, [updateFields]);


  
  const handleRedirectionImageLibrary = () => {
	setMediaSelection3(true)  
  }
  
  



  /////// Bloc 6 (Position QRcode dans video)
  
  const handleTimecodeChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    
    updateFields({ timecode: newValue });
    setTimecodeErrorMessage(testTimeCode(newValue));
  }, [updateFields]);

  const handleDurationChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    
    updateFields({ duration: newValue });
    setDurationErrorMessage(testDuration(newValue));
  }, [updateFields]);

  const handleAdPositionChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    updateFields({ adPosition: e.target.value });
  }, [updateFields]);


  /////// Bloc 7 (QR code)

  const handleNewImage = useCallback((file: File) => {
    updateFields({ resultImageFile: file });
  }, [updateFields]);


  




  /////// Bloc 8 (Messages)


  const handleCancelButton = () => {
    setShowRequest(false)
    setRequestMessage(<></>)
    setChoosenMedia(initializedMedia())
    setSalesActions([])
  }

  const handleValidateButton = () => {
    setShowRequest(false)
    setRequestMessage(<></>)
  }



 
const adFingerprint = useMemo(() => ({
  id: choosenProduct.id,
  productVariant: fields.productVariant,
  productImage: fields.productImage,
  alternateImageUrl: fields.alternateImageUrl,
  redirectionUuid: fields.redirectionUuid,

  // Texte
  comment1: salesAction?.comment1,
  comment2: salesAction?.comment2,

  // Couleurs
  shapeColor1: salesAction?.shapeColor1,
  shapeColor2: salesAction?.shapeColor2,
  backgroundColor1: salesAction?.backgroundColor1,
  backgroundColor2: salesAction?.backgroundColor2,

  // Options visuelles
  onlyQRcode: salesAction?.onlyQRcode,
  withImage: salesAction?.withImage,
  adPosition: salesAction?.adPosition,
}), [
  choosenProduct.id,
  fields.productVariant,
  fields.productImage,
  fields.alternateImageUrl,
  fields.redirectionUuid,
  salesAction
]);


function hashAd(obj: any) {
  const str = JSON.stringify(obj);
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = ((hash << 5) - hash) + str.charCodeAt(i);
    hash |= 0;
  }
  return hash.toString(36);
}

const updateKey = useMemo(
  () => hashAd(adFingerprint),
  [adFingerprint]
);

  useEffect(() => {
    generateAdvertisementCanvas({
      product:          choosenProduct,
      selectedVariant:  fields.productVariant,
      selectedImage:    fields.productImage,
      width:            fields.adWidth,
      pixelRatio:       4,
      onlyQRcode:       fields.onlyQRcode,
      comment1:         fields.comment1,
      comment2:         fields.comment2,
      shapeColor1:      fields.shapeColor1,
      shapeColor2:      fields.shapeColor2,
      backgroundColor1: fields.backgroundColor1,
      backgroundColor2: fields.backgroundColor2,
      redirectionUuid:  externalLink ? fields.redirectionUuid : undefined,
      withImage:        fields.withImage,
      alternateImage:   fields.alternateImageUrl,
    })
      .then(handleNewImage)
      .catch(e => console.error('❌ Erreur génération image canvas:', e));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateKey]);

// `${choosenProduct.id}-{fields.productVariant}-{fields.productImage}-${fields.redirectionUuid}-${fields.alternateImageUrl}`

  return (

    <div className={classNames(styles.root)}>

      {
        showRequest &&
        (
          <Window message                 = {requestMessage}
                  messageType             = "question"
                  onClickOnCancelButton   = {handleCancelButton}
                  onClickOnValidateButton = {handleValidateButton}
                  validButtonText         = "Oui"
                  cancelButtonText        = "Non" 
				  zIndex                  = {4000}/>
        )
      }

      <CollapsiblePanel title={title} onResize={onResize} onDelete={onDelete} open={open}>

        {
          mediaSelection &&
          <MediaSelector mediaSet        = {medias}
                         mediaCategories = {mediaCategories}
                         onCancel        = {handleMediaSelectorHide}
                         onSelect        = {handleMediaSelection} />
        }

        {
          mediaSelection2 &&
          <MediaSelector mediaSet        = {medias}
                         mediaCategories = {mediaCategories}
                         onCancel        = {handleMediaSelector2Hide}
                         onSelect        = {handleMediaSelection2} />
        }

        {
          mediaSelection3 &&
          <MediaSelector mediaSet        = {medias}
                         mediaCategories = {mediaCategories}
                         onCancel        = {handleMediaSelector3Hide}
                         onSelect        = {handleMediaSelection3} />
        }
		
        {
          productSelection &&
          <ProductSelector productSet        = {productArray}
                           productCategories = {categoryArray}
                           onCancel          = {handleProductSelectorHide}
                           onSelect          = {handleProductSelection} />
        }

        <div className = {classNames(styles.form)}>

          <div className = {classNames(styles.active)}>
            <WidgetFrame name               = "Actif :"
                         labelTextPosition  = "right"
                         labelBlockPosition = "left"
                         classNameGrid      = {classNames(styles.grid_frame)} >
              <Toggle checked  = {fields.active}
                      onChange = {handleActiveChange}
                      form     = "switch" />
            </WidgetFrame>
          </div>
		  
          <div className = {classNames(styles.name)}>
            <WidgetFrame name               = "Nom :"
                         labelTextPosition  = "right"
                         labelBlockPosition = "left"
                         classNameGrid      = {classNames(styles.grid_frame)} >

              <Field type        = "text"
                     placeholder = ""
                     value       = {fields.name}
                     onChange    = {handleNameChange} />

            </WidgetFrame>
          </div>
				
          <div className={classNames(styles.video)}>
            <WidgetFrame name               = "Video :" 
			             error              = {mediaErrorMessage} 
						 labelTextPosition  = "right" 
						 labelBlockPosition = "left" 
						 classNameGrid      = {classNames(styles.grid_frame)}>
              <div className={classNames(styles.centeredElements)}>
               {
				choosenMedia.id === 0 
				? 
				(
                 <Trigger name    = "Choisir une vidéo" 
				          onClick = {handleMediaSelectorShow} 
						  variant = "tailwind2" />
                ) 
			    : 
				(
                 <>
                   <div style={{ width: '100%', minHeight: '400px', display: 'flex', flexDirection: 'column' }}>
                      {/* ✅ MODIFICATION v1.2.0 : Passer currentMarker au VideoPlayer */}
                      <VideoPlayerWithAdvertisingSupport video                 = {choosenMedia as Video}
                                                         onGoToStart           = {handleVideoGoToStart}
                                                         onGoToEnd             = {handleVideoGoToEnd} 
														 existingMarkers       = {existingQrMarkers}
                                                         currentMarker         = {currentMarker}
														 currentSalesActionRef = {fieldsRef} />
                   </div>
                   <div className={classNames(styles.line_2, styles.text)}>
                     {choosenMedia.name}
                   </div>
                   <Trigger name    = "Choisir une autre vidéo" 
				            onClick = {handleMediaSelectorShow} 
							variant = "tailwind2" />
                 </>
                )
			   }
              </div>
            </WidgetFrame>
          </div>

          <div className={classNames(styles.produit)}>
            <WidgetFrame name="Produit :"
              labelWidth="90px"
              error={productErrorMessage}
              labelTextPosition="right"
              labelBlockPosition="left"
              classNameGrid={classNames(styles.grid_frame)} >

              <div className={classNames(styles.centeredElements)}>
                {
                  (choosenProduct.id === 0)
                    ?
                    (
                      <Trigger name="Choisir un produit" onClick={handleProductSelectorShow} variant="tailwind2" />
                    )
                    :
                    (
                      <>
                        {renderChoosenProduct()}
                        <Trigger name="Choisir un autre produit" onClick={handleProductSelectorShow} variant="tailwind2" />
                      </>
                    )
                }
              </div>

            </WidgetFrame>
          </div>

          <div className={classNames(styles.onlyQRcode)}>
            <WidgetFrame name="QR code seul :"
              labelTextPosition="right"
              labelBlockPosition="left"
              classNameGrid={classNames(styles.grid_frame)} >

              <Toggle checked={fields.onlyQRcode} onChange={handleOnlyQRcodeChange} form="switch" />

            </WidgetFrame>
          </div>

          {!fields.onlyQRcode &&
            <>
                <div className={classNames(styles.comment1)}>
                  <WidgetFrame name="Commentaire  1 :"
                    labelTextPosition="right"
                    labelBlockPosition="left"
                    classNameGrid={classNames(styles.grid_frame)} >

                    <Field type="text"
                      placeholder=""
                      value={fields.comment1}
                      onChange={handleComment1Change} />

                  </WidgetFrame>
                </div>

                <div className={classNames(styles.shapeColor1)}>
                  <WidgetFrame name="Texte :"
                    labelTextPosition="right"
                    labelBlockPosition="left"
                    labelWidth="60px"
                    classNameGrid={classNames(styles.grid_frame)} >

                    <FieldColor showField={false}
                      placeholder=""
                      value={fields.shapeColor1}
                      onChange={handleShapeColor1Change} />

                  </WidgetFrame>
                </div>

                <div className={classNames(styles.backgroundColor1)}>
                  <WidgetFrame name="Fond :"
                    labelTextPosition="right"
                    labelBlockPosition="left"
                    labelWidth="60px"
                    classNameGrid={classNames(styles.grid_frame)}>

                    <FieldColor showField={false}
                      placeholder=""
                      value={fields.backgroundColor1}
                      onChange={handleBackgroundColor1Change} />

                  </WidgetFrame>
                </div>


                <div className={classNames(styles.comment2)}>
                  <WidgetFrame name="Commentaire  2 :"
                    labelTextPosition="right"
                    labelBlockPosition="left"
                    classNameGrid={classNames(styles.grid_frame)} >

                    <Field type="text"
                      placeholder=""
                      value={fields.comment2}
                      onChange={handleComment2Change} />

                  </WidgetFrame>
                </div>

                <div className={classNames(styles.shapeColor2)}>
                  <WidgetFrame name="Texte :"
                    labelTextPosition="right"
                    labelBlockPosition="left"
                    labelWidth="60px"
                    classNameGrid={classNames(styles.grid_frame)}>

                    <FieldColor showField={false}
                      placeholder=""
                      value={fields.shapeColor2}
                      onChange={handleShapeColor2Change} />

                  </WidgetFrame>
                </div>

                <div className={classNames(styles.backgroundColor2)}>
                  <WidgetFrame name="Fond :"
                    labelTextPosition="right"
                    labelBlockPosition="left"
                    labelWidth="60px"
                    classNameGrid={classNames(styles.grid_frame)}>

                    <FieldColor showField={false}
                      placeholder=""
                      value={fields.backgroundColor2}
                      onChange={handleBackgroundColor2Change} />

                  </WidgetFrame>
                </div>

              <div className={classNames(styles.withImage)}>
                <WidgetFrame name="Avec image :"
                  labelTextPosition="right"
                  labelBlockPosition="left"
                  classNameGrid={classNames(styles.grid_frame)} >

              <Toggle checked  = {fields.withImage}
                      onChange = {handleWithImageChange}
                      form     = "switch" />

                </WidgetFrame>
              </div>
			  
              <div className = {classNames(styles.alternateImageUrl)}>
                <WidgetFrame name               = "Image (alt) :"
                             labelTextPosition  = "right"
                             labelBlockPosition = "left"
                             classNameGrid      = {classNames(styles.grid_frame)} >

                    <FieldMedia value         = {alternateImageName}
                                mediaURL      = {fields.alternateImageUrl ?? undefined}
                                onChange      = {handleAlternateImageChange}
								readOnly      = {false}
                                library       = {true}
                                upload        = {true}
                                showTextInput = {false} 
                                showPreview   = {true}
                                onDelete      = {handleAlternateImageDelete}
                                onShowLibrary = {handleAlternateImageLibrary} />
                </WidgetFrame>
              </div>

              <div className={classNames(styles.externalLink)}>
                <WidgetFrame name="Panier commerçant :"
                  labelTextPosition="right"
                  labelBlockPosition="left"
                  classNameGrid={classNames(styles.grid_frame)} >

                  <Toggle checked  = {externalLink}
                      onChange = {handleExternalLinkChange}
                      form     = "switch" />

                </WidgetFrame>
              </div>
			  
			  {
			   externalLink &&
               <>
                <div className={classNames(styles.redirectionTargetUrl)}>
                  <WidgetFrame name="URL cible :"
                    labelTextPosition="right"
                    labelBlockPosition="left"
                    classNameGrid={classNames(styles.grid_frame)} >

                    <Field type="text"
                      placeholder=""
                      value={fields.redirectionTargetUrl}
                      onChange={handleRedirectionTargetUrlChange} />

                  </WidgetFrame>
                </div>
                <div className={classNames(styles.redirectionUuid)}>
                  <WidgetFrame name="URL redirection :"
                    labelTextPosition="right"
                    labelBlockPosition="left"
                    classNameGrid={classNames(styles.grid_frame)} >

                    <div>{`${WWW_URLS.customer_front}/partenaire?redirect=${fields.redirectionUuid}`}</div>

                  </WidgetFrame>
                </div>
                <div className = {classNames(styles.redirectionImageUrl)}>
                  <WidgetFrame name               = "Image redirection :"
                               labelTextPosition  = "right"
                               labelBlockPosition = "left"
                               classNameGrid      = {classNames(styles.grid_frame)} >

                    <FieldMedia value         = {redirectionImageName}
                                mediaURL      = {fields.redirectionImageUrl ?? undefined}
                                onChange      = {handleRedirectionImageChange}
                                library       = {true}
                                upload        = {true}
								readOnly      = {false}
                                showTextInput = {false} 
                                showPreview   = {true}
                                onDelete      = {handleRedirectionImageDelete}
                                onShowLibrary = {handleRedirectionImageLibrary} />
                  </WidgetFrame>
                </div>			  
              </>
			  }
             </>
            }

          <div className={classNames(externalLink ? styles.timecode_2 : styles.timecode_1)}>
            <WidgetFrame name="Timecode :"
              error={timecodeErrorMessage}
              labelTextPosition="right"
              labelBlockPosition="left"
              classNameGrid={classNames(styles.grid_frame)} >
              <Field type="text"
                placeholder="hh:mm:ss"
                value={fields.timecode}
                onChange={handleTimecodeChange} />
            </WidgetFrame>
          </div>

          <div className={classNames(externalLink ? styles.duration_2 : styles.duration_1)}>
            <WidgetFrame name="Durée :"
              error={durationErrorMessage}
              labelTextPosition="right"
              labelBlockPosition="left"
              classNameGrid={classNames(styles.grid_frame2)} >
              <Field type="text"
                placeholder="hh:mm:ss"
                value={fields.duration}
                onChange={handleDurationChange} />
            </WidgetFrame>
          </div>

          <div className={classNames(externalLink ? styles.position_2 : styles.position_1)}>
            <WidgetFrame name="Position :"
              labelTextPosition="right"
              labelBlockPosition="left"
              classNameGrid={classNames(styles.grid_frame2)} >
              <Select choices={['left', 'right']}
                choicesLabel={['Gauche', 'Droite']}
                value={fields.adPosition}
                onChange={handleAdPositionChange} />
            </WidgetFrame>
          </div>

          <div className={classNames(styles.ad)}>
            <WidgetFrame name="QR code :"
              labelWidth="90px"
              error={productErrorMessage}
              labelTextPosition="right"
              labelBlockPosition="left"
              classNameGrid={classNames(styles.grid_frame)} >
			  
			  <div className={classNames(styles.qrCodeBlock)}>
                <Advertisement product          = {choosenProduct}
                               selectedVariant  = {fields.productVariant}
                               selectedImage    = {fields.productImage}
                               width            = {fields.adWidth}
                               onlyQRcode       = {fields.onlyQRcode}
                               comment1         = {fields.comment1}
                               comment2         = {fields.comment2}
                               shapeColor1      = {fields.shapeColor1}
                               shapeColor2      = {fields.shapeColor2}
                               backgroundColor1 = {fields.backgroundColor1}
                               backgroundColor2 = {fields.backgroundColor2}
                               redirectionUuid  = {externalLink ? fields.redirectionUuid : undefined}
                               withImage        = {fields.withImage}
                               alternateImage   = {fields.alternateImageUrl} />
			  </div>
            </WidgetFrame>

          </div>

        </div>

      </CollapsiblePanel>

    </div>

  );
};


export const SalesCampaignSheet_Action = React.memo(
  SalesCampaignSheet_ActionComponent,
  (prevProps, nextProps) => {
    const salesActionEqual = 
      prevProps.salesAction?.id === nextProps.salesAction?.id &&
      prevProps.salesAction?.active === nextProps.salesAction?.active &&
      prevProps.salesAction?.name === nextProps.salesAction?.name &&
      prevProps.salesAction?.mediaId === nextProps.salesAction?.mediaId &&
      prevProps.salesAction?.productId === nextProps.salesAction?.productId &&
      prevProps.salesAction?.productVariant === nextProps.salesAction?.productVariant &&
      prevProps.salesAction?.productImage === nextProps.salesAction?.productImage &&
      prevProps.salesAction?.timecode === nextProps.salesAction?.timecode &&
      prevProps.salesAction?.duration === nextProps.salesAction?.duration &&
      prevProps.salesAction?.adPosition === nextProps.salesAction?.adPosition &&
      prevProps.salesAction?.onlyQRcode === nextProps.salesAction?.onlyQRcode &&
      prevProps.salesAction?.comment1 === nextProps.salesAction?.comment1 &&
      prevProps.salesAction?.comment2 === nextProps.salesAction?.comment2 &&
      prevProps.salesAction?.shapeColor1 === nextProps.salesAction?.shapeColor1 &&
      prevProps.salesAction?.shapeColor2 === nextProps.salesAction?.shapeColor2 &&
      prevProps.salesAction?.backgroundColor1 === nextProps.salesAction?.backgroundColor1 &&
      prevProps.salesAction?.backgroundColor2 === nextProps.salesAction?.backgroundColor2 &&
      prevProps.salesAction?.withImage === nextProps.salesAction?.withImage &&
      prevProps.salesAction?.alternateImageUrl === nextProps.salesAction?.alternateImageUrl &&
      prevProps.salesAction?.redirectionTargetUrl === nextProps.salesAction?.redirectionTargetUrl &&
      prevProps.salesAction?.redirectionUuid === nextProps.salesAction?.redirectionUuid;
    
    return (
      salesActionEqual &&
      prevProps.isEditMode === nextProps.isEditMode &&
      prevProps.medias === nextProps.medias &&
      prevProps.mediaCategories === nextProps.mediaCategories &&
      prevProps.existingSalesCampaigns === nextProps.existingSalesCampaigns &&
      prevProps.onChange === nextProps.onChange &&
      prevProps.onResize === nextProps.onResize &&
      prevProps.onDelete === nextProps.onDelete &&
      prevProps.open === nextProps.open &&
      prevProps.name === nextProps.name &&
      prevProps.version === nextProps.version &&
      prevProps.showErrorMessages === nextProps.showErrorMessages
    );
  }
);

export default SalesCampaignSheet_Action;